import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import UsageInfo from '../../components/UsageInfo';
import LoadingSpinner from '../../components/LoadingSpinner';
import ModalShell from '../../components/ModalShell';

export default function OtelYonetimi() {
  const { personel, loginUser, refreshOtelAdi } = useAuth();
  const navigate = useNavigate();
  const [oteller, setOteller] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [modalAcik, setModalAcik] = useState(false);
  const [duzenle, setDuzenle] = useState(null);
  const [form, setForm] = useState({ ad: '', adres: '', telefon: '', email: '', vergiNo: '', vergiDairesi: '' });
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [silOnay, setSilOnay] = useState(null);
  const [siliniyor, setSiliniyor] = useState(false);
  const isDemoKullanici = !!personel?.isDemoOtel;

  const yukle = async () => {
    setYukleniyor(true);
    const data = await apiRequest('Otel');
    setOteller(data || []);
    setYukleniyor(false);
  };

  useEffect(() => { yukle(); }, []);

  const handleAc = (otel = null) => {
    if (otel) {
      setDuzenle(otel);
      setForm({ ad: otel.ad, adres: otel.adres || '', telefon: otel.telefon || '', email: otel.email || '', vergiNo: otel.vergiNo || '', vergiDairesi: otel.vergiDairesi || '' });
    } else {
      setDuzenle(null);
      setForm({ ad: '', adres: '', telefon: '', email: '', vergiNo: '', vergiDairesi: '' });
    }
    setModalAcik(true);
  };

  const handleKaydet = async () => {
    if (!form.ad.trim()) return;
    setKaydediliyor(true);
    if (duzenle) {
      await apiRequest(`Otel/${duzenle.otelId}`, 'PUT', { ...form, otelId: duzenle.otelId, aktifMi: duzenle.aktifMi });
    } else {
      await apiRequest('Otel', 'POST', form);
    }
    setModalAcik(false);
    setKaydediliyor(false);
    yukle();
    if (duzenle?.otelId === personel?.otelId) await refreshOtelAdi();
  };

  const handleSil = async (otelId) => {
    setSiliniyor(true);
    try {
      await apiRequest(`Otel/${otelId}`, 'DELETE');
      setOteller(p => p.filter(o => o.otelId !== otelId));
      setSilOnay(null);
    } catch (e) {
      alert(e?.message || 'Silme başarısız.');
    } finally {
      setSiliniyor(false);
    }
  };

  const handleOtelSec = async (otelId) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/otel-sec`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ personelId: personel?.personelId, otelId })
    });
    if (res.ok) {
      const data = await res.json();
      loginUser(data);
      window.location.href = '/';
    } else {
      const txt = await res.text().catch(() => '');
      alert('Otel değiştirilemedi: ' + (txt || res.status));
    }
  };

  const INP = "w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50 bg-white transition-all placeholder-gray-300 shadow-sm shadow-indigo-50/20";

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn" style={{ transform: 'translateZ(0)' }}>
      
      {/* Premium Header */}
      <div className="bg-white rounded-[2.5rem] border border-gray-200 shadow-sm p-6 md:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-indigo-50 rounded-full blur-3xl opacity-60 pointer-events-none" style={{ transform: 'translateZ(0)', willChange: 'transform' }}></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="flex items-center gap-6 min-w-0">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-2xl flex-shrink-0 flex items-center justify-center text-white text-3xl shadow-2xl shadow-indigo-200">🏨</div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter truncate uppercase">Otel Yönetimi</h1>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest truncate">İşletmenize bağlı tüm otelleri ve şube bilgilerini yönetin</p>
            </div>
          </div>
          {isDemoKullanici ? (
            <div className="px-8 py-4 bg-gray-100 text-gray-400 rounded-2xl text-[10px] font-black uppercase tracking-widest cursor-not-allowed" title="Demo hesaplarda yeni otel eklenemez">
              + Yeni Otel Ekle (Demo)
            </div>
          ) : (
            <button onClick={() => handleAc()}
              className="group relative px-8 py-4 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 hover:scale-105 active:scale-95 transition-all overflow-hidden">
              <span className="relative z-10 flex items-center gap-2">+ Yeni Otel Ekle</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            </button>
          )}
        </div>
      </div>

      {yukleniyor ? (
        <LoadingSpinner fullPage text="Veriler Yükleniyor..." color="indigo" size="lg" />
      ) : oteller.length === 0 ? (
        <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-20 text-center">
            <div className="w-24 h-24 bg-gray-50 rounded-[2rem] flex items-center justify-center text-5xl mx-auto mb-6 grayscale opacity-40">🏟️</div>
            <h3 className="text-xl font-black text-gray-800 uppercase tracking-tighter mb-2">Henüz Otel Bulunmuyor</h3>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-8">Sisteme ilk otelinizi ekleyerek yönetmeye başlayabilirsiniz.</p>
            <button onClick={() => handleAc()} className="text-indigo-600 font-black uppercase tracking-widest text-xs hover:underline decoration-2 underline-offset-8 transition-all">+ İLK OTEL KAYDINI OLUŞTUR</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {oteller.map(otel => {
            const isAktif = otel.otelId === personel?.otelId;
            return (
              <div key={otel.otelId} className={`relative bg-white rounded-[2.5rem] border-2 p-8 transition-[border-color,box-shadow,transform] duration-500 overflow-hidden group ${
                isAktif ? 'border-indigo-500 shadow-2xl shadow-indigo-100 ring-4 ring-indigo-50' : 'border-gray-100 hover:border-indigo-200 hover:shadow-lg'
              }`}>
                {isAktif && (
                   <div className="absolute top-0 right-0 p-4">
                      <span className="bg-indigo-600 text-white text-[9px] font-black px-4 py-1.5 rounded-full shadow-lg shadow-indigo-200 uppercase tracking-widest">KULLANILAN OTEL</span>
                   </div>
                )}
                
                <div className="flex items-start gap-6">
                  <div className={`w-16 h-16 rounded-3xl flex items-center justify-center text-white font-black text-2xl shadow-xl transition-all group-hover:scale-110 ${
                    isAktif ? 'bg-gradient-to-br from-indigo-500 to-indigo-700' : 'bg-gradient-to-br from-gray-400 to-gray-600 grayscale'
                  }`}>
                    {otel.ad.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-black text-gray-900 tracking-tighter truncate uppercase">{otel.ad}</h3>
                    <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest flex items-center gap-1.5">
                       📍 <span className="truncate">{otel.adres || 'ADRES BELİRTİLMEMİŞ'}</span>
                    </p>
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-2.5">
                  <div className="flex items-center gap-3 text-[11px] font-bold text-gray-600 bg-gray-50/50 px-4 py-3 rounded-2xl border border-gray-100/50">
                    <span className="text-sm">📞</span> {otel.telefon || '--'}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-bold text-gray-600 bg-gray-50/50 px-4 py-3 rounded-2xl border border-gray-100/50">
                    <span className="text-sm">✉️</span> {otel.email || '--'}
                  </div>
                  {(otel.vergiNo || otel.vergiDairesi) && (
                    <div className="flex items-center gap-3 text-[10px] font-bold text-indigo-400 bg-indigo-50/30 px-4 py-3 rounded-2xl border border-indigo-100/30 font-mono uppercase tracking-tighter">
                      🧾 {otel.vergiNo} / {otel.vergiDairesi}
                    </div>
                  )}
                </div>

                <div className="mt-8 flex gap-3">
                  {!isAktif && (
                    <button onClick={() => handleOtelSec(otel.otelId)}
                      className="flex-1 bg-white border-2 border-indigo-100 hover:bg-indigo-50 text-indigo-600 text-[10px] font-black py-4 rounded-2xl transition-[background-color,transform] duration-200 uppercase tracking-widest shadow-sm">
                      SİSTEMİ BU OTELE GEÇİR
                    </button>
                  )}
                  <button onClick={() => handleAc(otel)}
                    className="flex-1 bg-gray-900 hover:bg-gray-800 text-white text-[10px] font-black py-4 rounded-2xl transition-all uppercase tracking-widest shadow-xl shadow-gray-200">
                    DETAYLARI DÜZENLE
                  </button>
                  {!isAktif && !isDemoKullanici && oteller.filter(o => !o.isDemoOtel).length > 1 && (
                    <button onClick={() => setSilOnay(otel.otelId)}
                      className="px-5 py-4 bg-rose-50 hover:bg-rose-500 text-rose-500 hover:text-white text-[10px] font-black rounded-2xl transition-all uppercase tracking-widest border border-rose-100">
                      SİL
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {silOnay && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center px-4 backdrop-blur-md">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm text-center border border-gray-100">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50">🏨</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight uppercase">Oteli Sil</h3>
            <p className="text-[11px] font-black text-gray-500 mt-5 mb-10 leading-relaxed uppercase tracking-tight">Bu otel ve tüm bağlantılı verileri kalıcı olarak silinecek. Emin misiniz?</p>
            <div className="flex gap-4">
              <button onClick={() => setSilOnay(null)} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
              <button onClick={() => handleSil(silOnay)} disabled={siliniyor} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-200 transition-all uppercase tracking-widest active:scale-95">
                {siliniyor ? '⌛' : 'EVET, SİL'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <UsageInfo
        color="indigo"
        description="Otel Yönetimi sayfası üzerinden sistemdeki tüm şubelerinizi görebilir, yeni şube tanımları yapabilir ve aktif olarak kullandığınız oteli değiştirebilirsiniz."
        scenarios={[
          { title: "Aktif Otel Değişimi", content: "Birden fazla oteliniz varsa, 'Bu Otele Geç' butonu ile sistem verilerini (rezervasyonlar, misafirler, kasa) o otel bazında filtreleyecek şekilde değiştirebilirsiniz." },
          { title: "Ticari Bilgiler", content: "Otel kartlarındaki Vergi Numarası ve İletişim bilgileri faturalama ve web sitesi önizlemelerinde otomatik olarak kullanılır." },
          { title: "Yeni Şube Tanımı", content: "Yeni bir otel eklediğinizde sistem o otele özel bir alt yapı (odalar, personel yetkileri vb.) hazırlar." }
        ]}
      />

      {/* Otel Modal */}
      {modalAcik && createPortal(
        <ModalShell
          icon="🏛️"
          iconColor="from-indigo-600 to-blue-700"
          title={duzenle ? 'Otel Bilgilerini Güncelle' : 'Yeni Otel Kaydı'}
          subtitle="İşletmenizin resmi ve iletişim verileri"
          onClose={() => setModalAcik(false)}
          size="md"
          footer={
            <>
              <button onClick={() => setModalAcik(false)}
                className="px-8 py-3.5 border-2 border-gray-100 rounded-2xl text-[10px] font-black text-gray-400 uppercase tracking-widest hover:bg-gray-100 transition-all active:scale-95">İPTAL</button>
              <button onClick={handleKaydet} disabled={kaydediliyor || !form.ad.trim()}
                className="px-10 py-3.5 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:opacity-50 active:scale-95">
                {kaydediliyor ? 'KAYDEDİLİYOR...' : 'DEĞİŞİKLİKLERİ KAYDET'}
              </button>
            </>
          }
        >
          <div className="space-y-6 text-left">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-2">OTEL TİCARİ ADI *</label>
                <input className={INP} value={form.ad} onChange={e => setForm(f => ({ ...f, ad: e.target.value }))} placeholder="Örn: HotelFlow Premium Resort" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-2">TAM ADRES</label>
                <textarea className={INP + " resize-none"} rows={2} value={form.adres} onChange={e => setForm(f => ({ ...f, adres: e.target.value }))} placeholder="Cadde, Sokak, No, İlçe/İl" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-2">TELEFON</label>
                  <input className={INP} value={form.telefon} onChange={e => setForm(f => ({ ...f, telefon: e.target.value }))} placeholder="0212 ___ __ __" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-2">RESMİ E-POSTA</label>
                  <input className={INP} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="iletisim@otel.com" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-2">VERGİ NUMARASI</label>
                  <input className={INP} value={form.vergiNo} onChange={e => setForm(f => ({ ...f, vergiNo: e.target.value }))} placeholder="1234567890" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-2">VERGİ DAİRESİ</label>
                  <input className={INP} value={form.vergiDairesi} onChange={e => setForm(f => ({ ...f, vergiDairesi: e.target.value }))} placeholder="Beşiktaş V.D." />
                </div>
              </div>
            </div>
          </div>
          <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest leading-none text-center">Resmi bilgiler faturalama süreçlerinde kullanılacaktır.</p>
        </ModalShell>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
